import type { ICache, Cacheable } from "@auth0/auth0-spa-js";

// Same key prefix the SDK uses (CACHE_KEY_PREFIX in auth0-spa-js).
const CACHE_KEY_PREFIX = "@@auth0spajs@@";

/**
 * Auth0 token cache backed by sessionStorage.
 *
 * The SDK default is an in-memory cache, which is empty after every page reload, so a reload has to
 * re-authenticate through a hidden iframe on the Auth0 domain. That needs third-party cookies, and
 * when it fails the app falls through to the login page. This cache survives a reload in the same
 * tab but is dropped when the tab closes, and clearAuth() wipes it on logout.
 *
 * localStorage is deliberately not used: it is shared across tabs, and several screens call
 * localStorage.clear(), which would delete the tokens.
 */
export class SessionStorageCache implements ICache {
  public set<T = Cacheable>(key: string, entry: T) {
    sessionStorage.setItem(key, JSON.stringify(entry));
  }

  public get<T = Cacheable>(key: string): T | undefined {
    const json = sessionStorage.getItem(key);
    if (!json) return undefined;
    try {
      return JSON.parse(json) as T;
    } catch {
      return undefined;
    }
  }

  public remove(key: string) {
    sessionStorage.removeItem(key);
  }

  public allKeys() {
    return Object.keys(sessionStorage).filter((key) =>
      key.startsWith(CACHE_KEY_PREFIX),
    );
  }
}
